import { useState, useEffect } from "react";
import Axios from 'axios';
import { YouInvest, EQUITY, FUND, ETF, CEF, CONFIGURED_SERVICE_WATCHLISTS, CONFIGURED_SERVICE_ETF_SCREENER_TAB, CONFIGURED_SERVICE_SHARES_SCREENER_TAB, CONFIGURED_SERVICE_TRUSTS_SCREENER_TAB } from 'src/Api';
import { Loader } from 'src/Components/Molecules/Loader';
import {
    default as MorningstarAnalystRatingExternal,
} from "src/MarketDataComponents/MorningstarAnalystRatingExternal";
import moment from 'moment';
import Image from 'next/image';
import favicon from "src/Resources/Images/favicon.ico";
import { generateGuid } from 'src/Utils';
import {
    default as MorningstarSustainabilityRatingExternal,
} from "src/MarketDataComponents/MorningstarSustainabilityRatingExternal";
import { AddInvestmentModal } from 'src/Components/Molecules/Card';
import { Disclaimer } from 'src/Components/Molecules/Card';
import { formatNumber } from "src/MarketDataComponents/Helpers";
import { TablePagination } from 'src/Components/Molecules/Pagination';
import { useDetectClickOut } from "./index";
import { Button } from "@ajbellbusinessolution/fe-components";

const OVERVIEW_TAB = 'overview';
const PERFORMANCE_TAB = 'performance';
const PRICE_YIELD_TAB = 'price';
const RATINGS_TAB = 'ratings';
const RATIOS_TAB = 'ratios';

export const SECTOR = 'SectorId';
export const IMASECTOR = 'IMASectorId';
export const LARGESTSECTOR = 'LargestSector';
export const PROVIDER = 'AdministratorCompanyId';
export const ASSET = 'GlobalAssetClassId';
export const GLOBAL = 'Largestregion';
export const FUND_SIZE = 'FundTNAV';
export const YIELD = 'Yield_M12';
export const DIVIDEND_YIELD = 'DividendYield'
export const PERFORMANCE_1 = 'ReturnM12';
export const PERFORMANCE_3 = 'ReturnM36';
export const PERFORMANCE_5 = 'ReturnM60';
export const RISK_RATING = 'SRRI';
export const STAR_RATING = 'StarRating';
export const SUSTAINABILITY_RATING = 'SustainabilityRating';
export const ONGOING_CHARGE = 'LatestReportOngoingCharge';
export const DISTRIBUTION = 'Distribution';
export const MANAGEMENT = 'IndexFund';
export const FUNDS_SELECTION = 'FundsSelection';
export const INDEXING_APPROACH = 'IndexingApproach';
export const INDUSTRY = 'IndustryId';
export const PE_RATIO = 'PERatio';
export const EXCHANGE = 'ExchangeId';
export const REGION_EXCHANGE = 'RegionExchangeId';
export const MARKET_CAP = 'MarketCap';

const AnalystRatingLabels = {
    "3": "negative",
    "4": "neutral",
    "5": "bronze",
    "6": "silver",
    "7": "gold",
    "2": "underreview"
};

const ANNUAL_PERFORMANCE = 'Annual';
const TRAILING_PERFORMANCE = 'Trailing';

const SORT_ORDER_ASC = 'Asc';
const SORT_ORDER_DESC = 'Desc';

const BILLION_VALUE = 1000000000;
const MILLION_VALUE = 1000000;

export const SECTOR_MAPPING = {
    'Oil & Gas Producers': {
        sector: ['IG000BA009'],
        industry: ['IG000DA650', 'IG000DA655', 'IG000DA675', 'IG000DA660', 'IG000DA665', 'IG000DA670']
    }
}

let debounceTimer = 0;

const DRUPAL_INVESTMENT_PAGES = [
    '/shares',
    '/funds',
    '/etfs',
    '/investment-trusts'
];

export default function ResultsTable({ favourites, settings, type, query, screenerState, setNumberOfResults, setAutoCompleteItems }) {

    const [screenerData, setScreenerData] = useState(null);
    const [tableTab, setTableTab] = useState(OVERVIEW_TAB);
    const [selectedFilters, setSelectedFilters] = useState({
        [SECTOR]: [],
        [IMASECTOR]: [],
        [LARGESTSECTOR]: [],
        [PROVIDER]: [],
        [ASSET]: [],
        [FUND_SIZE]: [],
        [YIELD]: [],
        [DIVIDEND_YIELD]: [],
        [GLOBAL]: [],
        [RISK_RATING]: [],
        [STAR_RATING]: [],
        [SUSTAINABILITY_RATING]: [],
        [ONGOING_CHARGE]: [],
        [PERFORMANCE_1]: [],
        [PERFORMANCE_3]: [],
        [PERFORMANCE_5]: [],
        [DISTRIBUTION]: [],
        [MANAGEMENT]: [],
        [FUNDS_SELECTION]: [],
        [INDEXING_APPROACH]: [],
        [INDUSTRY]: [],
        [PE_RATIO]: [],
        [MARKET_CAP]: [],
        [EXCHANGE]: [],
        [REGION_EXCHANGE]: []
    });
    const [loading, setLoading] = useState(true);
    const [refresh, setRefresh] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [performanceType, setPerformanceType] = useState(ANNUAL_PERFORMANCE);
    const [sortOrder, setSortOrder] = useState(null);
    const [screenerType] = useState(type);
    const [watchlists, setWatchlists] = useState([]);
    const [watchlistsEnabled] = useState(YouInvest.getConfiguredServiceAllowed(CONFIGURED_SERVICE_WATCHLISTS, settings) || isBeta());
    const [reload, setReload] = useState(false);
    const [isGuest, setIsGuest] = useState(null);
    const [search, setSearch] = useState('');
    const [pageLoaded, setPageLoaded] = useState(false);
    const [isRedirected, setIsRedirected] = useState(false);

    const { setShow, show, nodeRef, triggerRef } = useDetectClickOut(false);

    const constructAutoCompleteItems = (screenData) => {
        const arrayResults = [];
        if (screenerData?.rows) {
            for (let i = 0; i < screenerData?.rows.length; i++) {
                arrayResults.push(
                    {
                        label: screenerData?.rows[i].name,
                        link: `/market-research/${screenerData?.rows[i].ExchangeCode ?? 'Fund'}:${screenerData?.rows[i].Symbol ?? screenerData?.rows[i].sedol}`
                    }
                )
            }
        }
        return arrayResults;
    }

    useEffect(() => {
        if (SECTOR_MAPPING[query.name]) {
            selectedFilters[SECTOR].push(...SECTOR_MAPPING[query.name].sector);
            selectedFilters[INDUSTRY].push(...SECTOR_MAPPING[query.name].industry);
        }

        Axios({
            method: 'GET',
            url: `/api/user/keep-alive`,
            headers: YouInvest.getTradingRequestHeaders()
        }).then((response) => {
            setIsGuest(false);
        }).catch((response) => {
            setIsGuest(true);
        });

        const navigationType = window?.performance?.getEntriesByType('navigation')?.[0]?.type;
        const referrer = document.referrer;
        const isInvestmentPage = DRUPAL_INVESTMENT_PAGES.some(page => referrer.includes(`/investment${page}`));

        if (navigationType === 'navigate' && referrer && isInvestmentPage) {
            setIsRedirected(true);
        }
    }, []);

    useEffect(() => {
        setNumberOfResults(screenerData?.total);
        setAutoCompleteItems(constructAutoCompleteItems());
    }, [screenerData, setAutoCompleteItems, setNumberOfResults, setScreenerData])

    useEffect(() => {
        const searchChanged = screenerState?.ScreenerAPIFetchState?.search !== search;
        const screenerType = screenerState?.ScreenerAPIFetchState?.screenerType || '';
        const screenerTypeMapping = {
            'Shares': 'Equity',
            'Funds': 'Fund',
            'Investment Trust': 'CEF'
        };

        const constructedPostData = {
            ...screenerState?.ScreenerAPIFetchState,
            sortOrder,
            currentPage: searchChanged ? 1 : currentPage,
            rowsPerPage,
            screenerType: screenerType ? screenerTypeMapping[screenerType] || screenerType : undefined
        };

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {

            const requestConfig = {
                method: 'POST',
                url: `${process.env.NEXT_PUBLIC_BASE_PATH}/api/screener`,
                data: constructedPostData,
                headers: YouInvest.getTradingRequestHeaders()
            };

            setLoading(true);
            Axios(requestConfig).then((response) => {
                setLoading(false);
                setScreenerData(response.data);
                setSearch(screenerState?.ScreenerAPIFetchState?.search);
                if (searchChanged) {
                    setCurrentPage(1);
                }
            }).catch((error) => {
                setLoading(false);
                console.error('Error fetching screener data:', error);
            });
        }, 500);

    }, [refresh, sortOrder, screenerState]);

    useEffect(() => {
        if (watchlistsEnabled) {
            setLoading(true);

            Axios({
                method: 'GET',
                url: `/api/v2/watchlists/index/get-watchlists`,
            }).then(response => {
                setLoading(false);
                setWatchlists(response.data);
            }).catch(error => {
                setLoading(false);
            });
        }
    }, [reload]);

    useEffect(() => {
        if (!loading && !pageLoaded && isRedirected) {
            setPageLoaded(true);
            const targetElement = document.getElementById("results-table");
            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [loading, isRedirected]);

    const getLoading = () => {
        return loading ? <Loader /> : null
    }

    const ChangeTableTab = (tab) => {
        setTableTab(tab);
    }

    const showStarRating = (rating) => {
        if (rating) {
            let numString;

            switch (rating) {
                case 1:
                    numString = 'One';
                    break;
                case 2:
                    numString = 'Two';
                    break;
                case 3:
                    numString = 'Three';
                    break;
                case 4:
                    numString = 'Four';
                    break;
                case 5:
                    numString = 'Five';
                    break;
            }

            return <div className={`ajb-rating-star--${numString.toLowerCase()}`} role="img" data-testid="morningStarRating" aria-label={`Morning Star Rating ${numString} Star`}></div>;
        }
    }

    const dealOnClick = (row) => {
        if (row && row.sedol) {
            window.location.href = `/platform/trade?marketCode=FUND:${row.sedol}`;
        }
    }

    const annualTrailingHandler = (event, type) => {
        event.preventDefault();

        setShow(!show);
        setPerformanceType(type);
    }

    const isFavouriteFund = (sedol) => {
        let isFavourite = false;

        favourites.map(fund => {
            if (fund.sedol === sedol && fund.belongsTo.includes('favoriteFunds')) {
                isFavourite = true;
            }
        });

        return isFavourite
    }

    const defaultTableRows = (row) => {
        let investment = '';

        if (screenerType === FUND) {
            investment = `FUND:${row.sedol}`;
        } else {
            investment = `${YouInvest.internationalExchangeCodeMapping(row.ExchangeCode)}:${row.Symbol}`;
        }

        return <>
            <td className="screenerField" scope="row" aria-label="Name">
                <a id="screener-investment-link" href={`/market-research/${investment}`} target="_blank" rel="noreferrer"><span>{row.name}</span></a>
                {screenerType !== EQUITY ? <>
                    <br />
                    <span className="h7 tooltipped">
                        {row.Distribution === "acc" ? <span aria-label="Any income the fund produces will be reinvested back into the fund by the manager, increasing the price of the units. Usually used by investors whose overall goal is growth." data-balloon-length="large" data-balloon-pos="right">
                            Accumulation
                        </span> : <span aria-label="Any income the fund produces will be paid into your AJ Bell account as cash. You can then decide what you want to do with it. Usually used by investors whose overall goal is income." data-balloon-length="large" data-balloon-pos="right">
                            Income
                        </span>}
                    </span>
                </> : null}

                {isFavouriteFund(row.sedol) ? <>
                    &nbsp;| <span className="tooltipped" aria-label="This is included in our Favourite fund list" data-balloon-pos="right">
                        <Image
                            alt=""
                            src={favicon}
                            width={20}
                            height={15}
                            className="padding-right-5"
                            style={{ marginBottom: "-4px" }} />
                    </span>
                </> : null}
            </td>
        </>
    }

    const sortOrderChangeHandler = (event, name, sortable) => {
        event.preventDefault();
        if (sortable) {
            let order = SORT_ORDER_DESC;

            if (sortOrder && sortOrder.name === name && sortOrder.order === SORT_ORDER_DESC) {
                order = SORT_ORDER_ASC;
            }

            setSortOrder({
                name,
                order
            });
        }
    }

    const sortableClassname = (name) => {
        let className = 'sortable nowrap';

        if (sortOrder && sortOrder.name === name) {
            className += ' active';
            if (sortOrder.order === SORT_ORDER_ASC) {
                className += ' asc';
            } else {
                className += ' desc';
            }
        }

        return className;
    }

    const displayNameHeader = () => {
        return <th className={sortableClassname('name')} onClick={(e) => { sortOrderChangeHandler(e, 'name', true) }}>Name</th>;
    }

    const displayHeaders = (headers) => {
        return headers.map(header => {
            return <th
                style={{ paddingTop: "40px" }}
                key={generateGuid()}
                className={`${!header.notSortable ? sortableClassname(header.type) : ''} tooltipped`}>
                <span
                    role="button"
                    tabIndex="0"
                    onClick={(e) => sortOrderChangeHandler(e, header.type, !header.notSortable)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') sortOrderChangeHandler(e, header.type, !header.notSortable) }}
                    aria-label={`Sort by ${header.name}`}
                >
                    {header.name}
                </span>
                {header.tooltip &&
                    <span style={{marginLeft: '3px'}} aria-label={header.tooltip} data-balloon-length={header.size} data-balloon-pos={header.size ? 'down' : 'up'}>
                        {header.tooltip && <i className="fa fa-question-circle" aria-hidden="true"></i>}
                    </span>
                }
            </th>
        });
    }

    const formatMarketCap = (marketCap) => {
        const convertedCap = parseFloat(marketCap);

        if (convertedCap < BILLION_VALUE) {
            return formatNumber((convertedCap / MILLION_VALUE), 2) + " mn";
        }

        return formatNumber((convertedCap / BILLION_VALUE), 2) + " bn";
    }

    const defaultEndTableRows = (row) => {
        let quote = { ...row };
        quote.__typename = screenerType;

        if (screenerType === FUND) {
            quote.marketCode = `FUND:${row.sedol}`;
            quote.lastPrice = row.Price;
        } else {
            quote.marketCode = `${row.ExchangeCode}:${row.Symbol}`;
            quote.askPrice = row.ClosePrice;
        }

        return <>
            <td>{watchlists && watchlistsEnabled ? <AddInvestmentModal quote={quote} watchlists={watchlists} reload={reload} setReload={setReload} isGuest={isGuest} isScreener={true} /> : null}</td>
            <td>
                <Button
                    className="screener-deal-button"
                    theme="dark"
                    chevron={'right'}
                    chevronSide={'right'}
                    onClick={() => dealOnClick(row)}>
                    Deal
                </Button>
            </td>
        </>
    }

    const displayOverviewTable = () => {
        let headers = [];

        if (screenerType !== EQUITY) {
            headers = [
                { name: 'Ongoing fund charge (%)', type: 'OngoingCharge', tooltip: "The % amount you'll pay for holding the fund, including the annual management fee. (This doesn't include additional costs such as transaction and performance fees).", size: 'large' },
                { name: 'YTD return (%)', type: 'ReturnM0', tooltip: "Year to date return  -  covers the performance of investments from today's date and the start of the current calendar year.", size: 'large' }
            ];

            if (screenerType === CEF) {
                headers.push(
                    { name: 'Discount/premium (%)', type: 'DiscountPremium', tooltip: "The difference between a trust’s price and it’s estimated NAV", size: 'large' }
                )
            }

            headers.push(
                { name: 'Morningstar rating™', type: 'rating', tooltip: "Morningstar rates investments from one to five stars based on how well they've performed in comparison to similar investments, after adjusting for risk and accounting for all relevant sales charges.", size: 'large' }
            );
        } else {
            headers = [
                { name: 'Currency', type: 'currency' },
                { name: 'Buy', type: 'Intraday.Ask', tooltip: 'A buy price is the price at which you can buy a particular investment in the market. It is also known as the “bid” price.', notSortable: true, size: 'large' },
                { name: 'Sell', type: 'Intraday.Bid', tooltip: 'A sell price is the price at which you can sell a particular investment in the market. It is also known as the “ask” price.', notSortable: true, size: 'large' },
                { name: 'Change (%)', type: 'ClosePriceChange', tooltip: "The percentage change in the last reported price for the share from the previous day.", size: 'large' },
                { name: 'Market capitalisation', type: 'MarketCap', tooltip: "The total value of all outstanding shares. Can be a useful measure of the company's size.", size: 'large' },
                { name: 'Yield (%)', type: 'DividendYield', tooltip: "The historical dividend per share divided by price. Can be used as an indication of the potential income to be earned from this share.", size: 'large' },
                { name: 'YTD return (%)', type: 'ReturnM0', tooltip: "Year to date return  -  covers the performance of investments from today's date and the start of the current calendar year.", size: 'large' }
            ];
        }

        return <table className="ajb-data-table ajb-data-table--small ajb-data-table--scroll screener-table">
            <thead>
                <tr>
                    {displayNameHeader()}
                    {displayHeaders(headers)}
                </tr>
            </thead>
            <tbody>
                {screenerData.rows.map((row) => {
                    return <tr key={generateGuid()}>
                        {defaultTableRows(row)}

                        {screenerType !== EQUITY ? <>
                            <td className="screenerField" aria-label="Ongoing fund charge (%)">{row.hasOwnProperty('OngoingCharge') ? row.OngoingCharge : '-'}</td>
                            <td className="screenerField" aria-label="YTD return (%)">{row.hasOwnProperty('ReturnM0') ? row.ReturnM0 : '-'}</td>
                            {screenerType === CEF ? <td className="screenerField" aria-label="Discount/premium (%)">{row.hasOwnProperty('DiscountPremium') ? row.DiscountPremium : '-'}</td> : null}
                            <td className="screenerField" aria-label="Morningstar rating™">{row.rating ? showStarRating(row.rating) : '-'}</td>
                        </> : <>
                            <td className="screenerField" aria-label="Currency">{row.hasOwnProperty('currency') ? row.currency : '-'}</td>
                            <td className="screenerField" aria-label="Buy">{row.hasOwnProperty('Intraday.Ask') ? row['Intraday.Ask'] : '-'}</td>
                            <td className="screenerField" aria-label="Sell">{row.hasOwnProperty('Intraday.Bid') ? row['Intraday.Bid'] : '-'}</td>
                            <td className="screenerField" aria-label="Change (%)">{row.hasOwnProperty('ClosePriceChange') ? row.ClosePriceChange : '-'}</td>
                            <td className="screenerField" aria-label="Market capitalisation">{row.hasOwnProperty('MarketCap') ? formatMarketCap(row.MarketCap) : '-'}</td>
                            <td className="screenerField" aria-label="Yield (%)">{row.hasOwnProperty('DividendYield') ? row.DividendYield : '-'}</td>
                            <td className="screenerField" aria-label="YTD return (%)">{row.hasOwnProperty('ReturnM0') ? row.ReturnM0 : '-'}</td>
                        </>}

                        {defaultEndTableRows(row)}
                    </tr>
                })}
            </tbody>
        </table>;
    }

    const displayPerformanceTable = () => {
        let year1 = moment().subtract(1, 'year').format('YYYY');
        let year2 = moment().subtract(2, 'year').format('YYYY');
        let year3 = moment().subtract(3, 'year').format('YYYY');
        let year4 = moment().subtract(4, 'year').format('YYYY');
        let year5 = moment().subtract(5, 'year').format('YYYY');

        return <table className="ajb-data-table ajb-data-table--small ajb-data-table--scroll screener-table">
            <thead>
                <tr>
                    {displayNameHeader()}
                    <th aria-label="More options" className="more-options text-align-center">
                        <span aria-label={performanceType === ANNUAL_PERFORMANCE ? "View the performance of investments with figures that cover the last 5 calendar years." : "View the performance of investments with figures that cover a rolling period whether annually, monthly, weekly, or daily."}>
                            <button ref={triggerRef} style={{ padding: "0.4em 1em 0.4em 0.6em", fontSize: "13px", width: "175px" }} className="ajb-custom-dropdown-trigger c-btn c-btn-smaller waves-effect waves-light button-outline button button--outline-darkblue c-btn-small no-arrow border-radius-0">
                                <span>{performanceType === ANNUAL_PERFORMANCE ? 'Annual' : 'Trailing'} performance</span>
                                <span className="ajb-close-custom-select-menu" id="ajb-close-custom-select-menu">{show ? <i className="fas fa-solid fa-times-circle padding-left-10 close"></i> : <i className="fa fa-chevron-down padding-left-10 open" aria-hidden="true"></i>}</span>
                            </button>
                        </span>

                        {show && <ul ref={nodeRef} role="listbox" className="dropdown-content dropdown-content-vault-menu" style={{ display: "block", right: "0px", transformOrigin: "0px 0px", opacity: 1, transform: "scaleX(1) scaleY(1)", width: "250px" }}>
                            <li role="option" aria-selected="false">
                                <a onClick={(e) => annualTrailingHandler(e, ANNUAL_PERFORMANCE)} href="">
                                    <input type="radio" id="Annualperformance" name="performances" className="ajb-form-input" checked={performanceType === ANNUAL_PERFORMANCE} />
                                    <label htmlFor="Annualperformance" className="ajb-form-label">Annual performance</label>
                                </a>
                            </li>
                            <li role="option" aria-selected="false">
                                <a onClick={(e) => annualTrailingHandler(e, TRAILING_PERFORMANCE)} href="">
                                    <input type="radio" id="Trailingperformance" name="performances" className="ajb-form-input" checked={performanceType === TRAILING_PERFORMANCE} />
                                    <label htmlFor="Trailingperformance" className="ajb-form-label">Trailing performance</label>
                                </a>
                            </li>
                        </ul>}
                    </th>
                    {performanceType === ANNUAL_PERFORMANCE ? <>
                        {displayHeaders([
                            { name: (year1 + ' (%)'), type: 'YR_ReturnM12_1' },
                            { name: (year2 + ' (%)'), type: 'YR_ReturnM12_2' },
                            { name: (year3 + ' (%)'), type: 'YR_ReturnM12_3' },
                            { name: (year4 + ' (%)'), type: 'YR_ReturnM12_4' },
                            { name: (year5 + ' (%)'), type: 'YR_ReturnM12_5' }
                        ])}
                    </> : <>
                        {displayHeaders([
                            { name: '1W (%)', type: 'ReturnW1' },
                            { name: '1M (%)', type: 'ReturnM1' },
                            { name: '3M (%)', type: 'ReturnM3' },
                            { name: '6M (%)', type: 'ReturnM6' },
                            { name: '1Y (%)', type: 'ReturnM12' },
                            { name: '3Y (%)', type: 'ReturnM36' },
                            { name: '5Y (%)', type: 'ReturnM60' },
                            { name: '10Y (%)', type: 'ReturnM120' }
                        ])}
                    </>}
                    <th></th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {screenerData.rows.map(row => {
                    return <tr key={generateGuid()}>
                        {defaultTableRows(row)}

                        <td className="screenerField" aria-label="Annual / Trailing performance">{performanceType} performance</td>
                        {performanceType === ANNUAL_PERFORMANCE ? <>
                            <td className="screenerField" aria-label={year1 + ' (%)'}>{row.hasOwnProperty('YR_ReturnM12_1') ? row.YR_ReturnM12_1 : '-'}</td>
                            <td className="screenerField" aria-label={year2 + ' (%)'}>{row.hasOwnProperty('YR_ReturnM12_2') ? row.YR_ReturnM12_2 : '-'}</td>
                            <td className="screenerField" aria-label={year3 + ' (%)'}>{row.hasOwnProperty('YR_ReturnM12_3') ? row.YR_ReturnM12_3 : '-'}</td>
                            <td className="screenerField" aria-label={year4 + ' (%)'}>{row.hasOwnProperty('YR_ReturnM12_4') ? row.YR_ReturnM12_4 : '-'}</td>
                            <td className="screenerField" aria-label={year5 + ' (%)'}>{row.hasOwnProperty('YR_ReturnM12_5') ? row.YR_ReturnM12_5 : '-'}</td>
                        </> : <>
                            <td className="screenerField" aria-label='1W (%)'>{row.hasOwnProperty('ReturnW1') ? row.ReturnW1 : '-'}</td>
                            <td className="screenerField" aria-label='1M (%)'>{row.hasOwnProperty('ReturnM1') ? row.ReturnM1 : '-'}</td>
                            <td className="screenerField" aria-label='3M (%)'>{row.hasOwnProperty('ReturnM3') ? row.ReturnM3 : '-'}</td>
                            <td className="screenerField" aria-label='6M (%)'>{row.hasOwnProperty('ReturnM6') ? row.ReturnM6 : '-'}</td>
                            <td className="screenerField" aria-label='1Y (%)'>{row.hasOwnProperty('ReturnM12') ? row.ReturnM12 : '-'}</td>
                            <td className="screenerField" aria-label='3Y (%)'>{row.hasOwnProperty('ReturnM36') ? row.ReturnM36 : '-'}</td>
                            <td className="screenerField" aria-label='5Y (%)'>{row.hasOwnProperty('ReturnM60') ? row.ReturnM60 : '-'}</td>
                            <td className="screenerField" aria-label='10Y (%)'>{row.hasOwnProperty('ReturnM120') ? row.ReturnM120 : '-'}</td>
                        </>}

                        {defaultEndTableRows(row)}
                    </tr>;
                })}
            </tbody>
        </table>;
    }

    const displayPriceYieldRowsTable = () => {
        const headersList = [{ name: 'Currency', type: 'priceCurrencyId' }];

        if (screenerType !== ETF && screenerType !== CEF) {
            headersList.push({ name: 'Price', type: 'Price', tooltip: "The most recent price reported by the fund. For a unit trust or OEIC, NAV per share normally corresponds to the fund's market price.", size: 'large' });
        } else {
            headersList.push(
                { name: 'Buy', type: 'Intraday.Ask', tooltip: 'A buy price is the price at which you can buy a particular investment in the market. It is also known as the “bid” price.', notSortable: true },
                { name: 'Sell', type: 'Intraday.Bid', tooltip: 'A sell price is the price at which you can sell a particular investment in the market. It is also known as the “ask” price.', notSortable: true },
            );
        }

        headersList.push(
            { name: 'Change (%)', type: 'ClosePriceChange', tooltip: "The percentage change in the last reported price for the fund from the previous day.", size: 'large' },
            { name: 'Yield (%)', type: 'Yield_M12', tooltip: "The income the fund has returned to investors, expressed as a percentage. You can use this to gauge the potential income you could earn from the fund.", size: 'large' }
        );

        return <table className="ajb-data-table ajb-data-table--small ajb-data-table--scroll screener-table">
            <thead>
                <tr>
                    {displayNameHeader()}
                    {displayHeaders(headersList)}
                </tr>
            </thead>
            <tbody>
                {screenerData.rows.map(row => {
                    return <tr key={generateGuid()}>
                        {defaultTableRows(row)}

                        <td className="screenerField" aria-label="Currency">{row.hasOwnProperty('priceCurrencyId') ? (row.priceCurrencyId).replace('CU$$$$$', '') : '-'}</td>
                        {screenerType !== ETF && screenerType !== CEF ? <td className="screenerField" aria-label="Price">
                            {row.hasOwnProperty('Price') ? row.Price : '-'}
                        </td> : <>
                            <td className="screenerField" aria-label="Buy">
                                {row.hasOwnProperty('Intraday.Ask') ? row['Intraday.Ask'] : '-'}
                            </td>
                            <td className="screenerField" aria-label="Sell">
                                {row.hasOwnProperty('Intraday.Bid') ? row['Intraday.Bid'] : '-'}
                            </td>
                        </>}
                        <td className="screenerField" aria-label="Change (%)">{row.hasOwnProperty('ClosePriceChange') ? row.ClosePriceChange : '-'}</td>
                        <td className="screenerField" aria-label="Yield (%)">{row.hasOwnProperty('Yield_M12') ? formatNumber(row.Yield_M12, 2) : '-'}</td>

                        {defaultEndTableRows(row)}
                    </tr>;
                })}
            </tbody>
        </table>;
    }

    const displayRatingsRowsTable = () => {
        return <table className="ajb-data-table ajb-data-table--small ajb-data-table--scroll screener-table">
            <thead>
                <tr>
                    {displayNameHeader()}
                    {displayHeaders([
                        { name: 'Morningstar Medalist Rating™', type: 'Medalist_RatingNumber', tooltip: "The Analyst Rating is the summary expression of Morningstar’s forward-looking analysis of a fund. Morningstar analysts assign the ratings on a five-tier scale with three positive ratings of Gold, Silver, and Bronze, a Neutral rating, and a Negative rating.", size: 'large' },
                        { name: 'Morningstar Rating™', type: 'rating', tooltip: "Morningstar rates investments from one to five stars based on how well they've performed in comparison to similar investments, after adjusting for risk and accounting for all relevant sales charges.", size: 'large' },
                        { name: 'Morningstar Sustainability Rating™', type: 'SustainabilityRating', tooltip: "The Morningstar Sustainability Rating is a reliable indicator to see if funds are meeting ESG challenges.", size: 'large' }
                    ])}
                </tr>
            </thead>
            <tbody>
                {screenerData.rows.map(row => {
                    return <tr key={generateGuid()}>
                        {defaultTableRows(row)}

                        <td>{row.Medalist_RatingNumber ? <MorningstarAnalystRatingExternal rating={AnalystRatingLabels[row.Medalist_RatingNumber]} noTooltip={true} /> : '-'}</td>
                        <td>{row.rating ? showStarRating(row.rating) : '-'}</td>
                        <td>{row.SustainabilityRating ? <div style={{ marginBottom: "-10px" }}><MorningstarSustainabilityRatingExternal rating={Number(row.SustainabilityRating)} noTooltip={true} /></div> : '-'}</td>

                        {defaultEndTableRows(row)}
                    </tr>;
                })}
            </tbody>
        </table>;
    }

    const displayRatiosRowsTable = () => {
        return <table className="ajb-data-table ajb-data-table--small ajb-data-table--scroll screener-table">
            <thead>
                <tr>
                    {displayNameHeader()}
                    {screenerType !== EQUITY ? displayHeaders([
                        { name: 'Morningstar Medalist Rating™', type: 'Medalist_RatingNumber', tooltip: "The Analyst Rating is the summary expression of Morningstar’s forward-looking analysis of a fund. Morningstar analysts assign the ratings on a five-tier scale with three positive ratings of Gold, Silver, and Bronze, a Neutral rating, and a Negative rating.", size: 'large' },
                        { name: 'Morningstar Rating™', type: 'rating', tooltip: "Morningstar rates investments from one to five stars based on how well they've performed in comparison to similar investments, after adjusting for risk and accounting for all relevant sales charges.", size: 'large' },
                        { name: 'Morningstar Sustainability Rating™', type: 'SustainabilityRating', tooltip: "The Morningstar Sustainability Rating is a reliable indicator to see if funds are meeting ESG challenges.", size: 'large' }
                    ]) : displayHeaders([
                        { name: 'Return on equity', type: 'ROEYear1', tooltip: "Return on Equity (ROE) is the measure of a company's net income divided by its total shareholders' equity for the companys last financial year.", size: 'large' },
                        { name: 'Debt/Equity Ratio', type: 'DebtEquityRatio', tooltip: 'The Debt-to-Equity ratio shows how much debt a company has compared to its assets.' },
                        { name: 'P/E ratio', type: 'PERatio', tooltip: 'Price-to-earnings ratio. This is calculated by dividing the current share price by current earnings per share. P/E ratios are an indication of how investors value a company.' },
                        { name: 'P/E - 5 year average ratio', type: 'PERatio5YrAverage', tooltip: 'A shares average price to earnings ratio over the last five-years.' },
                        { name: 'Earning per share', type: 'DilutedContinuousEPSGrowthYear1', tooltip: 'A company’s total earnings divided by the current number of shares outstanding for their last financial year. Earning per share helps gauges the profitability of the company from the view of the shareholders.', size: 'large' },
                    ])}
                </tr>
            </thead>
            <tbody>
                {screenerData.rows.map(row => {
                    return <tr key={generateGuid()}>
                        {defaultTableRows(row)}

                        {screenerType !== EQUITY ? <>
                            <td className="screenerField" aria-label="Morningstar Medalist Rating™">{row.Medalist_RatingNumber ? <MorningstarAnalystRatingExternal rating={AnalystRatingLabels[row.Medalist_RatingNumber]} noTooltip={true} /> : '-'}</td>
                            <td className="screenerField" aria-label="Morningstar Rating™">{row.rating ? showStarRating(row.rating) : '-'}</td>
                            <td className="screenerField" aria-label="Morningstar Sustainability Rating™">{row.SustainabilityRating ? <div style={{ marginBottom: "-10px" }}><MorningstarSustainabilityRatingExternal rating={Number(row.SustainabilityRating)} noTooltip={true} /></div> : '-'}</td>
                        </> : <>
                            <td className="screenerField" aria-label="Return on equity">{row.hasOwnProperty('ROEYear1') ? formatNumber((row.ROEYear1), 2) : '-'}</td>
                            <td className="screenerField" aria-label="Debt Equity Ratio">{row.hasOwnProperty('DebtEquityRatio') ? formatNumber((row.DebtEquityRatio), 2) : '-'}</td>
                            <td className="screenerField" aria-label="P/E ratio">{row.hasOwnProperty('PERatio') ? formatNumber((row.PERatio), 2) : '-'}</td>
                            <td className="screenerField" aria-label="P/E - 5 year average ratio">{row.hasOwnProperty('PERatio5YrAverage') ? formatNumber((row.PERatio5YrAverage), 2) : '-'}</td>
                            <td className="screenerField" aria-label="Earning per share - 1 year">{row.hasOwnProperty('DilutedContinuousEPSGrowthYear1') ? formatNumber((row.DilutedContinuousEPSGrowthYear1), 2) : '-'}</td>
                        </>}

                        {defaultEndTableRows(row)}
                    </tr>;
                })}
            </tbody>
        </table>;
    }

    const displayTable = () => {
        switch (tableTab) {
            case OVERVIEW_TAB:
                return displayOverviewTable();
            case PERFORMANCE_TAB:
                return displayPerformanceTable();
            case PRICE_YIELD_TAB:
                return displayPriceYieldRowsTable();
            case RATINGS_TAB:
                return displayRatingsRowsTable();
            case RATIOS_TAB:
                return displayRatiosRowsTable();
        }
    }

    const handleChangePage = (page) => {
        const startAt = page * rowsPerPage;
        if (page != 0 && startAt < (screenerData.total + rowsPerPage)) {
            setCurrentPage(page);
            setRefresh(!refresh);
        }
    }

    const handleChangeRowsPerPage = (event) => {
        const { value } = event.target;

        setRowsPerPage(value * 1);
        setCurrentPage(1);
        setRefresh(!refresh);
    }

    return <>
        <div className="ajb-col-8-breakpoint main-content-wrapper white-bg">

            <div className="ajb-card">
                <div className="ajb-grid">
                    <div className="ajb-card-content ajb-tabs-stage-content active" id="ajb-tab-1" style={{ display: "block" }}>
                        <div className="ajb-grid-row margin-top-0-sm">

                            <div className="ajb-grid ajb-tabs-nav">
                                <div className="ajb-tabs-nav-wrapper">
                                    <Button
                                        className={`${tableTab === OVERVIEW_TAB ? "view-change-button-selected" : "view-change-button"}`}
                                        onClick={() => ChangeTableTab(OVERVIEW_TAB)}>
                                        Overview
                                    </Button>

                                    <Button
                                        className={`${tableTab === PERFORMANCE_TAB ? "view-change-button-selected" : "view-change-button"}`}
                                        onClick={() => ChangeTableTab(PERFORMANCE_TAB)}>
                                        Performance
                                    </Button>

                                    {screenerType !== EQUITY ? <>
                                        <Button
                                            className={`${tableTab === PRICE_YIELD_TAB ? "view-change-button-selected" : "view-change-button"}`}
                                            onClick={() => ChangeTableTab(PRICE_YIELD_TAB)}>
                                            Prices and yield
                                        </Button>

                                        <Button
                                            className={`${tableTab === RATINGS_TAB ? "view-change-button-selected" : "view-change-button"}`}
                                            onClick={() => ChangeTableTab(RATINGS_TAB)}>
                                            Ratings
                                        </Button>
                                    </> : <>
                                        <Button
                                            className={`${tableTab === RATIOS_TAB ? "view-change-button-selected" : "view-change-button"}`}
                                            onClick={() => ChangeTableTab(RATIOS_TAB)}>
                                            Ratios
                                        </Button>
                                    </>}
                                </div>
                            </div>


                            <div className="ajb-tabs-stage">
                                <div className="ajb-card-content ajb-tabs-stage-content" id="ajb-tab-111" style={{ display: "block" }}>
                                    <div className="ajb-grid">
                                        <div className="ajb-data-table-wrapper ajb-data-table-overflow-hidden-sm">
                                            {screenerData ? displayTable() : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {screenerData ? <div className="ajb-grid-row text-align-center clearfix">
                            <TablePagination
                                count={screenerData.total}
                                page={currentPage}
                                onChangePage={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                screener={true}
                            />
                        </div> : null}
                    </div>
                </div>
            </div>

            <Disclaimer />

        </div>

        {getLoading()}
    </>
}
